import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import {
  readSchoolId,
  SchoolLegalHeader,
  useSchoolLegalBranding,
} from '../components/SchoolLegalBranding';
import { describeRules, freeEntryDocument, rulesDocument } from '../utils/dreamRules';
import { REPLACED_RULES_NOTICE, loadRules, readRulesDocument } from '../utils/officialRules';
import '../styles/PublicPages.css';
import '../styles/OfficialRules.css';

function Block({ block }) {
  if (block.kind === 'list') {
    const Tag = block.ordered ? 'ol' : 'ul';
    return (
      <Tag className={block.ordered ? 'terms-list official-rules-steps' : 'terms-list'}>
        {block.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </Tag>
    );
  }
  if (block.link) {
    return (
      <p className="terms-text">
        {block.before}
        <a href={block.link.href} className="terms-link">
          {block.link.text}
        </a>
        {block.after}
      </p>
    );
  }
  return <p className="terms-text">{block.text}</p>;
}

function Blocks({ blocks }) {
  return blocks.map((block, i) => <Block key={i} block={block} />);
}

// The sweepstakes rules the server filled in for the period or the prize.
function DocumentRules({ doc, replaced }) {
  return (
    <>
      {replaced ? <p className="terms-text official-rules-notice">{REPLACED_RULES_NOTICE}</p> : null}
      <div className="terms-intro">
        <h2 className="terms-section-title">{doc.title}</h2>
        {doc.notice ? <p className="terms-text official-rules-notice">{doc.notice}</p> : null}
        {doc.intro.map((text, i) => (
          <p className="terms-text" key={i}>
            {text}
          </p>
        ))}
      </div>
      {doc.sections.map((section) => (
        <div className="terms-section" key={section.heading}>
          <h2 className="terms-section-title">{section.heading}</h2>
          {section.paragraphs.map((text, i) => (
            <p className="terms-text" key={i}>
              {text}
            </p>
          ))}
          {section.list.length ? <Block block={{ kind: 'list', items: section.list }} /> : null}
        </div>
      ))}
    </>
  );
}

function WordedRules({ ctx }) {
  const official = rulesDocument(ctx);
  return (
    <>
      <div className="terms-intro">
        <p className="terms-text">{official.intro}</p>
      </div>
      {official.sections.map((section) => (
        <div className="terms-section" key={section.title}>
          <h2 className="terms-section-title">{section.title}</h2>
          <Blocks blocks={section.blocks} />
        </div>
      ))}
    </>
  );
}

function RulesText({ rules, replaced }) {
  const ctx = describeRules(rules);
  const doc = readRulesDocument(rules && rules.document);
  const free = freeEntryDocument(ctx);
  return (
    <>
      {doc ? <DocumentRules doc={doc} replaced={replaced} /> : <WordedRules ctx={ctx} />}
      <p className="terms-text">
        <a href="#free-entry" className="terms-link">
          How to enter for free
        </a>
      </p>

      <div className="terms-section official-rules-free-entry" id="free-entry">
        <h2 className="terms-section-title">How to enter for free</h2>
        <p className="terms-text">{free.intro}</p>
        {free.sections.map((section) => (
          <div className="terms-subsection" key={section.title}>
            <h3 className="terms-subsection-title">{section.title}</h3>
            <Blocks blocks={section.blocks} />
          </div>
        ))}
        <p className="terms-text">
          <Link to="/support/" className="terms-link">
            Message support
          </Link>
        </p>
        <p className="terms-text">
          <a href="#official-rules" className="terms-link">
            Official rules
          </a>
        </p>
      </div>
    </>
  );
}

// Public page at the summary's rules_url (?schoolId=<uuid>, plus &prizeId=<uuid> for
// one prize draw or bonus drop): the Dream official rules, readable without a sign-in.
const OfficialRules = () => {
  const [searchParams] = useSearchParams();
  const schoolId = readSchoolId(searchParams);
  const prizeId = String(searchParams.get('prizeId') || '').trim();
  const branding = useSchoolLegalBranding();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', rules: null, replaced: false });

  useEffect(() => {
    if (!schoolId) {
      setState({ status: 'missing', rules: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', rules: null });
    loadRules((id, prize) => apiService.getDreamRules(id, prize), schoolId, prizeId)
      .then(({ rules, replaced }) => {
        if (!cancelled) setState({ status: 'ready', rules, replaced });
      })
      .catch((error) => {
        if (cancelled) return;
        const code = error && error.response ? error.response.status : 0;
        // A 4xx means this school has no rules to show; anything else may pass on a retry.
        setState({ status: code >= 400 && code < 500 ? 'missing' : 'error', rules: null });
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId, prizeId, attempt]);

  let body;
  if (state.status === 'ready') {
    body = <RulesText rules={state.rules} replaced={state.replaced} />;
  } else if (state.status === 'missing') {
    body = <p className="terms-text">Official rules are not available for this school.</p>;
  } else if (state.status === 'error') {
    body = (
      <>
        <p className="terms-text">We couldn't load the official rules.</p>
        <button
          type="button"
          className="terms-link official-rules-retry"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </button>
      </>
    );
  } else {
    body = <p className="terms-text">Loading official rules...</p>;
  }

  return (
    <div
      className={
        branding.isSchool ? 'staff-page legal-page legal-page--school' : 'staff-page legal-page'
      }
      style={branding.style}
    >
      <div className="auth-container">
        <SchoolLegalHeader branding={branding} />

        <div className="auth-form">
          <div className="terms-content">
            <h1 className="terms-main-title" id="official-rules">
              Official rules
            </h1>
            {body}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficialRules;
